import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  apiRequest,
  getAuthToken,
  getAuthUser,
  clearAuthToken,
  uploadFiles,
  API_BASE,
} from '../lib/api';
import { wchIspReadUID, wchIspFlash } from '../lib/wch';
import { computeMd5 } from '../lib/md5';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import HomeBuilderTab from '../components/admin/HomeBuilderTab';
import ImageManager from '../components/admin/ImageManager';
import FirmwaresTab from '../components/admin/FirmwaresTab';
import Pagination from '../components/Pagination';
import { showToast } from '../components/Toast';
import { CardSkeletonLoader, TableSkeletonLoader, DashboardSkeletonLoader, FormSkeletonLoader } from '../components/SkeletonLoader';
import DeviceHistoryModal from '../components/DeviceHistoryModal';
import { formatVnDate, formatVnDateTime } from '../lib/dateUtils';

// ─── Types ────────────────────────────────────────────────────────────────────
type MetricItem = { name: string; count: number };
type AnalyticsStats = {
  totalViews: number; uniqueVisitors: number; productsCount: number;
  postsCount: number; firmwaresCount: number; openTicketsCount: number;
  chartData: { date: string; views: number }[];
  topProducts: MetricItem[]; topPosts: MetricItem[];
  topBuys: MetricItem[]; topFirmwares: MetricItem[];
};

type AdminUser = {
  id: string; email: string; name: string | null;
  phone: string | null; role: 'ADMIN' | 'USER'; createdAt: string;
  _count: { ownerships: number };
};

type AdminDevice = {
  id: string; deviceUid: string;
  model: string; source: string;
  status: string; warrantyMonths: number;
  createdAt: string;
  ownerships: { user: { id: string; email: string; name: string | null; phone?: string | null } }[];
  warrantyRecords: {
    activatedAt: string;
    expiresAt: string;
    status: string;
    activationReason?: string;
    activationNote?: string | null;
  }[];
  flashLogs?: {
    flashedAt: string;
    result: string;
    firmware?: {
      id: string;
      version: string;
      bmcuModel: string;
      printerModel: string;
      amsSlot?: string | null;
      retractLen?: string | null;
    } | null;
  }[];
};

export type Tab = 'dashboard' | 'users' | 'devices' | 'firmwares' | 'settings' | 'home' | 'tickets';

// ─── Shared Styles ────────────────────────────────────────────────────────────
const card = {
  background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)',
  borderRadius: '16px', padding: '24px', boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
};

const th: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontSize: '12px',
  fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid var(--glass-border)',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '12px 14px', fontSize: '14px', color: '#f1f5f9',
  borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'middle',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function Badge({ children, color = 'rgba(255,255,255,0.1)', textColor = '#cbd5e1', title }: { children: React.ReactNode; color?: string; textColor?: string; title?: string }) {
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px',
      borderRadius: '999px', fontSize: '12px', fontWeight: 600,
      background: color, color: textColor,
      cursor: title ? 'help' : undefined,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

function ActionBtn({ onClick, danger, children }: { onClick: () => void; danger?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
      cursor: 'pointer', border: 'none',
      background: danger ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.1)',
      color: danger ? '#f87171' : '#cbd5e1',
      transition: 'all 0.15s',
    }}
      onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
    >
      {children}
    </button>
  );
}

function DeviceActionDropdown({
  onHistory,
  onDelete,
}: {
  onHistory: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 140;
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(10, rect.right + window.scrollX - menuWidth),
      });
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: open ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: open ? '#f8fafc' : '#cbd5e1',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: 1,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = '#fff';
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#cbd5e1';
          }
        }}
        title="Thao tác"
      >
        ⋮
      </button>

      {open &&
        createPortal(
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: menuPos.top + 'px',
              left: menuPos.left + 'px',
              zIndex: 99999,
              minWidth: '140px',
              background: '#0f172a',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: '10px',
              padding: '6px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.7), 0 8px 10px -6px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onHistory();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#e2e8f0',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>📋</span>
              <span>Lịch sử</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#f87171',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
                marginTop: '2px',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>🗑️</span>
              <span>Xoá</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}

function UserActionDropdown({
  onDetail,
  onDelete,
}: {
  onDetail: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 140;
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(10, rect.right + window.scrollX - menuWidth),
      });
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: open ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: open ? '#f8fafc' : '#cbd5e1',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: 1,
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = '#fff';
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#cbd5e1';
          }
        }}
        title="Thao tác"
      >
        ⋮
      </button>

      {open &&
        createPortal(
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: menuPos.top + 'px',
              left: menuPos.left + 'px',
              zIndex: 99999,
              minWidth: '140px',
              background: '#0f172a',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: '10px',
              padding: '6px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.7), 0 8px 10px -6px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onDetail();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#e2e8f0',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>👁️</span>
              <span>Chi tiết</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: 'transparent',
                border: 'none',
                color: '#f87171',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s ease',
                marginTop: '2px',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '14px' }}>🗑️</span>
              <span>Xoá</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}



function CopyPhone({ phone }: { phone: string }) {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phone);
    showToast(`Đã sao chép: ${phone}`, 'info');
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Bấm để sao chép số điện thoại"
      style={{
        background: 'rgba(56, 189, 248, 0.12)',
        border: '1px solid rgba(56, 189, 248, 0.25)',
        borderRadius: '6px',
        padding: '3px 8px',
        color: '#38bdf8',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(56, 189, 248, 0.22)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(56, 189, 248, 0.12)')}
    >
      <span>📞</span>
      <span>{phone}</span>
    </button>
  );
}

function SectionTitle({ title, count }: { title: string; count?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
      <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#f8fafc' }}>{title}</h2>
      {count !== undefined && (
        <Badge>{count}</Badge>
      )}
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────
function UsersTab({ showToast }: { showToast: (msg: string, type?: 'info' | 'error' | 'warning') => void }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ users: AdminUser[] }>('/admin/users', undefined, true);
      setUsers(res.users);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const nonAdminUsers = users.filter(u => u.role !== 'ADMIN');
  const filteredUsers = nonAdminUsers.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.phone && u.phone.includes(q))
    );
  });

  const totalPages = Math.ceil(filteredUsers.length / pageSize) || 1;
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  async function performDeleteUser() {
    if (!confirmDeleteUser) return;
    const target = confirmDeleteUser;
    setConfirmDeleteUser(null);
    setUsers(prev => prev.filter(u => u.id !== target.id));
    try {
      await apiRequest(`/admin/users/${target.id}`, { method: 'DELETE' }, true);
      showToast(`Đã xoá tài khoản ${target.email}`, 'info');
      // Optimistic update: item already removed from state, no re-fetch needed
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi xoá tài khoản', 'error');
      await loadUsers(); // Rollback on error
    }
  }

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <SectionTitle title="Người dùng" count={nonAdminUsers.length} />
          <input
            type="text"
            placeholder="🔍 Tìm theo Email, họ tên hoặc SĐT..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{
              padding: '10px 14px',
              borderRadius: '10px',
              border: '1px solid var(--glass-border)',
              fontSize: '14px',
              width: '100%',
              maxWidth: '320px',
              boxSizing: 'border-box',
              background: '#131823',
              color: '#f8fafc',
              outline: 'none',
            }}
          />
        </div>
        {loading ? <TableSkeletonLoader rows={4} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Họ tên</th>
                  <th style={th}>Số điện thoại</th>
                  <th style={th}>Vai trò</th>
                  <th style={th}>BMCU sở hữu</th>
                  <th style={th}>Ngày tạo</th>
                  <th style={{ ...th, textAlign: 'center' }}>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map(u => (
                  <tr key={u.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{u.email}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{u.name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {u.phone ? <CopyPhone phone={u.phone} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <Badge
                        color={u.role === 'ADMIN' ? '#fef3c7' : '#f1f5f9'}
                        textColor={u.role === 'ADMIN' ? '#92400e' : '#475569'}
                      >
                        {u.role === 'ADMIN' ? '🔑 Admin' : '👤 User'}
                      </Badge>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => setSelectedUser(u)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#60a5fa', fontWeight: 600, fontSize: '14px',
                      }}>
                        {u._count.ownerships} thiết bị
                      </button>
                    </td>
                    <td style={{ ...td, color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {formatVnDate(u.createdAt)}
                    </td>
                    <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <UserActionDropdown
                        onDetail={() => setSelectedUser(u)}
                        onDelete={() => setConfirmDeleteUser(u)}
                      />
                    </td>
                  </tr>
                ))}
                {paginatedUsers.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '32px' }}>Không tìm thấy người dùng nào</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filteredUsers.length}
          pageSize={pageSize}
          onPageChange={setPage}
          label="người dùng"
        />
      </div>

      {/* User Device Modal */}
      {selectedUser && (
        <UserDeviceModal user={selectedUser} onClose={() => { setSelectedUser(null); }} />
      )}

      {/* Confirm Delete User Modal */}
      {confirmDeleteUser && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setConfirmDeleteUser(null)}>
          <div style={{
            background: 'var(--dark-bg)', border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '32px',
            width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Xác nhận xoá</h3>
              <button onClick={() => setConfirmDeleteUser(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5, marginBottom: '32px' }}>
              Xoá tài khoản <strong style={{ color: '#f8fafc' }}>{confirmDeleteUser.email}</strong>? Hành động này không thể hoàn tác!
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setConfirmDeleteUser(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)',
                color: '#cbd5e1', border: 'none', fontWeight: 600, cursor: 'pointer',
              }}>
                Hủy
              </button>
              <button type="button" onClick={performDeleteUser} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: '#dc2626',
                color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer',
              }}>
                Xác nhận xoá
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserDeviceModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  type UserDetail = AdminUser & {
    address?: string | null;
    ownerships: {
      id: string;
      startAt: string;
      device: AdminDevice;
    }[];
  };
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiRequest<{ user: UserDetail }>(`/admin/users/${user.id}`, undefined, true)
      .then(res => setDetail(res.user))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const profile = detail || user;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0e121a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '20px',
          padding: '28px',
          maxWidth: '680px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          color: '#f8fafc',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.25), rgba(147, 51, 234, 0.25))',
                border: '1px solid rgba(225, 29, 72, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}
            >
              👤
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>
                Thông tin người dùng
              </h3>
              <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '13px' }}>
                Hồ sơ tài khoản & các thiết bị BMCU sở hữu
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#94a3b8',
              transition: 'all 0.15s ease',
            }}
          >
            ×
          </button>
        </div>

        {/* User Profile Card */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '14px',
            padding: '18px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '14px',
            }}
          >
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Họ và tên</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>
                {profile.name || <span style={{ color: '#64748b' }}>Chưa cập nhật</span>}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Email tài khoản</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#60a5fa', wordBreak: 'break-all' }}>
                {profile.email}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Số điện thoại</div>
              <div style={{ fontSize: '14px' }}>
                {profile.phone ? (
                  <CopyPhone phone={profile.phone} />
                ) : (
                  <span style={{ color: '#64748b' }}>Chưa cập nhật</span>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Vai trò hệ thống</div>
              <div>
                <Badge
                  color={profile.role === 'ADMIN' ? '#fef3c7' : '#f1f5f9'}
                  textColor={profile.role === 'ADMIN' ? '#92400e' : '#475569'}
                >
                  {profile.role === 'ADMIN' ? '🔑 Admin' : '👤 Khách hàng'}
                </Badge>
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Địa chỉ liên hệ</div>
              <div style={{ fontSize: '13px', color: detail?.address ? '#e2e8f0' : '#64748b' }}>
                {detail?.address || 'Chưa cập nhật'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '3px' }}>Ngày đăng ký</div>
              <div style={{ fontSize: '13px', color: '#cbd5e1' }}>
                {formatVnDate(profile.createdAt)}
              </div>
            </div>
          </div>
        </div>

        {/* Devices Section Title */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>
              Danh sách BMCU sở hữu
            </span>
            <Badge color="rgba(56, 189, 248, 0.15)" textColor="#38bdf8">
              {detail ? detail.ownerships.length : user._count?.ownerships || 0}
            </Badge>
          </div>
        </div>

        {/* Devices List */}
        {loading ? (
          <CardSkeletonLoader count={2} />
        ) : !detail || detail.ownerships.length === 0 ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '12px',
              border: '1px dashed rgba(255, 255, 255, 0.1)',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>Tài khoản này chưa liên kết thiết bị BMCU nào</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {detail.ownerships.map(o => {
              const d = o.device;
              const warranty = d.warrantyRecords?.[0];
              const latestLog = d.flashLogs?.[0];
              const fw = latestLog?.firmware;
              const loadMode = fw?.printerModel ? (fw.printerModel.match(/\((.*?)\)/)?.[1] || fw.printerModel) : '';
              const retractStr = fw?.retractLen ? (fw.retractLen.endsWith('m') ? fw.retractLen : fw.retractLen + 'm') : '0.10m';

              return (
                <div
                  key={o.id}
                  style={{
                    padding: '16px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  {/* Row 1: UID + Model + Warranty Badge */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: '14px',
                          color: '#f8fafc',
                          background: 'rgba(255, 255, 255, 0.06)',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        {d.deviceUid}
                      </span>
                      <Badge color="rgba(59, 130, 246, 0.15)" textColor="#93c5fd">
                        {d.model}
                      </Badge>
                      <Badge
                        color={d.source === 'SHOP' ? '#dbeafe' : '#dcfce7'}
                        textColor={d.source === 'SHOP' ? '#1e40af' : '#166534'}
                      >
                        {d.source === 'SHOP' ? '🏪 Shop' : '✅ External'}
                      </Badge>
                    </div>

                    {/* Warranty status */}
                    {d.warrantyMonths === 0 ? (
                      <Badge color="rgba(255, 255, 255, 0.05)" textColor="#94a3b8">
                        Không bảo hành
                      </Badge>
                    ) : warranty ? (
                      (() => {
                        const isExpired = new Date(warranty.expiresAt) <= new Date();
                        const daysLeft = Math.ceil((new Date(warranty.expiresAt).getTime() - Date.now()) / 86400000);
                        return isExpired ? (
                          <Badge color="rgba(239, 68, 68, 0.15)" textColor="#f87171">
                            ❌ Hết bảo hành
                          </Badge>
                        ) : (
                          <Badge color="rgba(34, 197, 94, 0.15)" textColor="#4ade80">
                            🛡️ Còn BH ({daysLeft} ngày)
                          </Badge>
                        );
                      })()
                    ) : (
                      <Badge color="rgba(234, 179, 8, 0.15)" textColor="#facc15">
                        ⏳ Chờ kích hoạt
                      </Badge>
                    )}
                  </div>

                  {/* Row 2: Details grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#94a3b8',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <div>
                      <span style={{ color: '#64748b' }}>Bảo hành: </span>
                      <span style={{ color: '#cbd5e1' }}>{d.warrantyMonths} tháng</span>
                      {warranty && (
                        <span style={{ color: '#94a3b8' }}>
                          {' '}({formatVnDate(warranty.activatedAt)} → {formatVnDate(warranty.expiresAt)})
                        </span>
                      )}
                    </div>

                    <div>
                      <span style={{ color: '#64748b' }}>Ngày kích hoạt: </span>
                      <span style={{ color: '#cbd5e1' }}>{formatVnDate(o.startAt)}</span>
                    </div>

                    {fw && (
                      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#64748b' }}>FW nạp gần nhất:</span>
                        <span
                          style={{
                            background: 'rgba(56, 189, 248, 0.15)',
                            color: '#38bdf8',
                            border: '1px solid rgba(56, 189, 248, 0.3)',
                            padding: '0 5px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 700,
                            lineHeight: '16px',
                          }}
                        >
                          v{fw.version}
                        </span>
                        <span style={{ color: '#cbd5e1', fontWeight: 500 }} title={fw.printerModel}>
                          {loadMode}
                        </span>
                        <span style={{ color: '#475569' }}>•</span>
                        <span style={{ color: '#cbd5e1' }}>{fw.amsSlot || 'AMS_A'}</span>
                        <span style={{ color: '#475569' }}>•</span>
                        <span style={{ color: '#cbd5e1' }}>
                          Retract: {retractStr}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Devices Tab ─────────────────────────────────────────────────────────────
function DevicesTab({ showToast }: { showToast: (msg: string, type?: 'info' | 'error' | 'warning') => void }) {
  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ deviceUid: '', model: '', source: 'SHOP' as 'SHOP' | 'EXTERNAL_APPROVED', warrantyMonths: 12 });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [confirmDeleteDevice, setConfirmDeleteDevice] = useState<AdminDevice | null>(null);

  const [settings, setSettings] = useState<{ bmcu_models: string[], default_firmware_url?: string }>({ bmcu_models: [] });
  const [showHistory, setShowHistory] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [flashingStatus, setFlashingStatus] = useState<'idle' | 'flashing' | 'success' | 'error'>('idle');
  const [flashingLog, setFlashingLog] = useState<string>('');
  const [flashingProgress, setFlashingProgress] = useState<number>(0);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const [devicesRes, settingsRes] = await Promise.all([
        apiRequest<{ devices: AdminDevice[] }>('/admin/devices', undefined, true),
        apiRequest<{ settings: any }>('/admin/settings', undefined, true)
      ]);
      setDevices(devicesRes.devices);
      setSettings({
        bmcu_models: settingsRes.settings?.bmcu_models || [],
        default_firmware_url: settingsRes.settings?.default_firmware_url
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  useEffect(() => {
    if (showAdd || showHistory || confirmDeleteDevice) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showAdd, showHistory, confirmDeleteDevice]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiRequest<{ device: AdminDevice }>('/admin/devices', { method: 'POST', body: JSON.stringify(form) }, true);
      setDevices(prev => [
        { ...response.device, ownerships: [], warrantyRecords: [] },
        ...prev,
      ]);
      showToast('Đã thêm thiết bị thành công', 'info');
      setShowAdd(false);
      setFlashingStatus('idle');
      setForm({ deviceUid: '', model: '', source: 'SHOP', warrantyMonths: 12 });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi thêm thiết bị', 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteDeviceClick(device: AdminDevice) {
    const owner = device.ownerships[0]?.user;
    if (owner) {
      showToast(`Không thể xoá, thiết bị đang thuộc về ${owner.email}`, 'error');
      return;
    }
    setConfirmDeleteDevice(device);
  }

  async function performDeleteDevice() {
    if (!confirmDeleteDevice) return;
    const target = confirmDeleteDevice;
    setConfirmDeleteDevice(null);
    setDevices(prev => prev.filter(d => d.id !== target.id));
    try {
      await apiRequest(`/admin/devices/${target.id}`, { method: 'DELETE' }, true);
      showToast(`Đã xoá thiết bị ${target.deviceUid}`, 'info');
      // Optimistic update: item already removed from state, no re-fetch needed
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi xoá thiết bị', 'error');
      await loadDevices(); // Rollback on error
    }
  }

  async function loadHistory(deviceId: string) {
    setHistoryLoading(true);
    setShowHistory(deviceId);
    try {
      const data = await apiRequest<any>(`/device/${deviceId}/history`, undefined, true);
      setHistoryData(data);
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function readFromUSB() {
    setFlashingStatus('idle');
    setFlashingLog('');
    setFlashingProgress(0);

    try {
      if (!('serial' in navigator)) {
        showToast("Trình duyệt của bạn không hỗ trợ Web Serial API. Vui lòng sử dụng Chrome hoặc Edge trên máy tính.", 'warning');
        return;
      }
      const port = await (navigator as any).serial.requestPort();

      const uid = await wchIspReadUID(port, (s) => console.log('[USB Admin]', s), false);
      if (uid) {
        setForm(prev => ({ ...prev, deviceUid: uid.trim() }));

        if (settings.default_firmware_url) {
          setFlashingStatus('flashing');
          setFlashingLog('Đang tải Firmware cơ bản...');
          try {
            const fwUrl = settings.default_firmware_url;
            const absoluteUrl = fwUrl.startsWith('http') ? fwUrl : (API_BASE.replace('/api', '') + fwUrl);
            const response = await fetch(absoluteUrl);
            if (!response.ok) throw new Error('Không tải được file firmware từ server');
            const arrayBuffer = await response.arrayBuffer();
            const fwData = new Uint8Array(arrayBuffer);
            const md5 = computeMd5(fwData);

            setFlashingLog(`Đã tải xong (${fwData.length.toLocaleString()} bytes, MD5: ${md5.substring(0, 8)}...). Đang nạp vào chip...`);
            const success = await wchIspFlash(port, fwData, (s) => setFlashingLog(s), (pct) => setFlashingProgress(pct));
            if (success) {
              setFlashingStatus('success');
              setFlashingLog('Nạp Firmware thành công! Thiết bị đã sẵn sàng.');
            } else {
              setFlashingStatus('error');
              setFlashingLog('Lỗi: Nạp Firmware thất bại.');
            }
          } catch (err: any) {
            console.error(err);
            setFlashingStatus('error');
            setFlashingLog("Lỗi: " + err.message);
          }
        } else {
          setFlashingStatus('success');
          setFlashingLog('Đã đọc UID thành công. Không có FW cơ bản để nạp.');
        }

      } else {
        setFlashingStatus('error');
        setFlashingLog("Lỗi: Không thể đọc UID thiết bị.");
      }

      // Cleanup port if needed, though wchIsp routines usually leave it in a certain state.
      try { await port.close(); } catch (e) { }
    } catch (err: any) {
      console.error(err);
      setFlashingStatus('error');
      setFlashingLog("Lỗi kết nối cổng USB. Thiết bị có thể đã ngắt kết nối.");
    }
  }

  const filtered = devices.filter(d => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      d.deviceUid.toLowerCase().includes(q) ||
      d.model.toLowerCase().includes(q) ||
      d.ownerships[0]?.user.email.toLowerCase().includes(q) ||
      d.ownerships[0]?.user.name?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedDevices = filtered.slice((page - 1) * pageSize, page * pageSize);

  const inputStyle: React.CSSProperties = {
    padding: '11px 14px', borderRadius: '10px', border: '1px solid var(--glass-border)',
    fontSize: '14px', width: '100%', boxSizing: 'border-box', background: '#131823', color: '#f8fafc',
    outline: 'none',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    backgroundSize: '16px',
    paddingRight: '40px',
  };

  useEffect(() => {
    if (showAdd || showHistory || confirmDeleteDevice) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showAdd, showHistory, confirmDeleteDevice]);

  return (
    <div>
      <div style={card}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <SectionTitle title="Quản lý BMCU" count={devices.length} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍 Tìm theo UID hoặc Model..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid var(--glass-border)',
                fontSize: '13px',
                width: '260px',
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#131823',
                color: '#f8fafc',
                outline: 'none',
              }}
            />
            <button type="button" onClick={() => {
              setShowAdd(!showAdd);
              if (!showAdd) setFlashingStatus('idle');
            }} style={{
              padding: '10px 18px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #e11d48, #be123c)',
              color: 'white', border: 'none', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)',
              whiteSpace: 'nowrap',
            }}>
              + Thêm BMCU
            </button>
          </div>
        </div>

        {/* Add Form Modal */}
        {showAdd && createPortal(
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', zIndex: 100000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}>
            <div style={{
              background: '#0e121a', border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '32px',
              width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)', color: '#f8fafc'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Thêm thiết bị BMCU mới</h3>
                <button type="button" onClick={() => { setShowAdd(false); setFlashingStatus('idle'); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
              </div>

              <form onSubmit={handleAdd}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>
                      Device UID <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input required style={{ ...inputStyle, flex: 1 }} value={form.deviceUid} placeholder="VD: BMCU-A1B2C3D4"
                        onChange={e => setForm({ ...form, deviceUid: e.target.value })} disabled={flashingStatus === 'flashing'} />
                      <button type="button" onClick={readFromUSB} disabled={flashingStatus === 'flashing'} style={{
                        padding: '0 16px', borderRadius: '10px',
                        background: flashingStatus === 'flashing' ? 'rgba(255,255,255,0.05)' : 'rgba(225, 29, 72, 0.15)',
                        color: flashingStatus === 'flashing' ? '#64748b' : '#fb7185',
                        border: `1px solid ${flashingStatus === 'flashing' ? 'var(--glass-border)' : 'rgba(225, 29, 72, 0.35)'}`,
                        fontWeight: 600, fontSize: '13px', cursor: flashingStatus === 'flashing' ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px',
                        transition: 'all 0.15s ease'
                      }}>🔌 Đọc từ thiết bị</button>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>
                      Model <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select required style={selectStyle} value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}>
                      <option value="" style={{ background: '#0e121a', color: '#cbd5e1' }}>Chọn Model...</option>
                      {settings.bmcu_models.map(m => (
                        <option key={m} value={m} style={{ background: '#0e121a', color: '#f8fafc' }}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>Nguồn gốc</label>
                    <select style={selectStyle} value={form.source}
                      onChange={e => setForm({ ...form, source: e.target.value as 'SHOP' | 'EXTERNAL_APPROVED' })}>
                      <option value="SHOP" style={{ background: '#0e121a', color: '#f8fafc' }}>🏪 Shop</option>
                      <option value="EXTERNAL_APPROVED" style={{ background: '#0e121a', color: '#f8fafc' }}>✅ External Approved</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#cbd5e1', display: 'block', marginBottom: '6px' }}>Thời hạn bảo hành</label>
                    <select style={selectStyle} value={form.warrantyMonths}
                      onChange={e => setForm({ ...form, warrantyMonths: parseInt(e.target.value) })}>
                      <option value={0} style={{ background: '#0e121a', color: '#f8fafc' }}>Không bảo hành</option>
                      <option value={3} style={{ background: '#0e121a', color: '#f8fafc' }}>3 tháng</option>
                      <option value={6} style={{ background: '#0e121a', color: '#f8fafc' }}>6 tháng</option>
                      <option value={9} style={{ background: '#0e121a', color: '#f8fafc' }}>9 tháng</option>
                      <option value={12} style={{ background: '#0e121a', color: '#f8fafc' }}>12 tháng</option>
                      <option value={15} style={{ background: '#0e121a', color: '#f8fafc' }}>15 tháng</option>
                      <option value={18} style={{ background: '#0e121a', color: '#f8fafc' }}>18 tháng</option>
                    </select>
                  </div>
                </div>
                {flashingStatus !== 'idle' && (
                  <div style={{
                    marginTop: '18px', padding: '14px 16px', borderRadius: '12px',
                    background: flashingStatus === 'error' ? 'rgba(239, 68, 68, 0.12)' : flashingStatus === 'success' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(56, 189, 248, 0.12)',
                    border: `1px solid ${flashingStatus === 'error' ? 'rgba(239, 68, 68, 0.35)' : flashingStatus === 'success' ? 'rgba(34, 197, 94, 0.35)' : 'rgba(56, 189, 248, 0.35)'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: flashingStatus === 'flashing' ? '10px' : '0', fontSize: '13px', fontWeight: 600, color: flashingStatus === 'error' ? '#f87171' : flashingStatus === 'success' ? '#4ade80' : '#38bdf8' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {flashingStatus === 'success' ? '✓' : flashingStatus === 'error' ? '⚠' : '⏳'}
                        {flashingLog}
                      </span>
                      {flashingStatus === 'flashing' && <span style={{ color: '#38bdf8', fontWeight: 700 }}>{Math.round(flashingProgress)}%</span>}
                    </div>
                    {flashingStatus === 'flashing' && (
                      <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'linear-gradient(90deg, #38bdf8, #818cf8)', width: `${flashingProgress}%`, transition: 'width 0.2s' }}></div>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="button" onClick={() => { setShowAdd(false); setFlashingStatus('idle'); }} style={{
                    flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)',
                    color: '#cbd5e1', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: 'pointer',
                  }}>
                    Hủy
                  </button>
                  <button type="submit" disabled={saving || flashingStatus === 'flashing' || flashingStatus === 'error'} style={{
                    flex: 1, padding: '12px', borderRadius: '10px', background: (saving || flashingStatus === 'flashing' || flashingStatus === 'error') ? '#475569' : 'linear-gradient(135deg, #e11d48, #be123c)',
                    color: 'white', border: 'none', fontWeight: 600, cursor: (saving || flashingStatus === 'flashing' || flashingStatus === 'error') ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)'
                  }}>
                    {saving ? 'Đang lưu...' : 'Lưu thiết bị'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}



        {/* Table */}
        {loading ? <TableSkeletonLoader rows={5} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Device UID</th>
                  <th style={th}>Thiết bị & Firmware</th>
                  <th style={th}>Nguồn</th>
                  <th style={th}>Chủ sở hữu</th>
                  <th style={th}>Ngày thêm</th>
                  <th style={th}>Bảo hành</th>
                  <th style={{ ...th, textAlign: 'center' }}>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDevices.map(d => {
                  const owner = d.ownerships[0]?.user;
                  const warranty = d.warrantyRecords?.[0];
                  const latestLog = d.flashLogs?.[0];
                  const fw = latestLog?.firmware;
                  return (
                    <tr key={d.id}>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{d.deviceUid}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '13px', whiteSpace: 'nowrap' }}>{d.model}</div>
                        {fw ? (
                          (() => {
                            const loadMode = fw.printerModel.match(/\((.*?)\)/)?.[1] || fw.printerModel;
                            const retractStr = fw.retractLen ? (fw.retractLen.endsWith('m') ? fw.retractLen : fw.retractLen + 'm') : '0.10m';
                            return (
                              <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                  <span style={{
                                    background: 'rgba(56, 189, 248, 0.15)',
                                    color: '#38bdf8',
                                    border: '1px solid rgba(56, 189, 248, 0.3)',
                                    padding: '0 5px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    lineHeight: '16px'
                                  }}>
                                    v{fw.version}
                                  </span>
                                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#cbd5e1' }} title={fw.printerModel}>
                                    {loadMode}
                                  </span>
                                </div>
                                <div style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                  <span>{fw.amsSlot || 'AMS_A'}</span>
                                  <span style={{ margin: '0 4px', color: '#475569' }}>•</span>
                                  <span>Retract: {retractStr}</span>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div style={{ marginTop: '2px', fontSize: '11px', color: '#64748b' }}>—</div>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <Badge
                          color={d.source === 'SHOP' ? '#dbeafe' : '#dcfce7'}
                          textColor={d.source === 'SHOP' ? '#1e40af' : '#166534'}
                        >
                          {d.source === 'SHOP' ? '🏪 Shop' : '✅ External'}
                        </Badge>
                      </td>
                      <td style={td}>
                        {owner ? (
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600 }}>{owner.name || owner.email}</div>
                            {owner.name && <div style={{ fontSize: '12px', color: '#64748b' }}>{owner.email}</div>}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '13px' }}>Chưa có chủ</span>
                        )}
                      </td>
                      <td style={{ ...td, color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        {formatVnDate(d.createdAt)}
                      </td>
                      <td style={{ ...td, fontSize: '13px' }}>
                        {d.warrantyMonths === 0 ? (
                          <Badge color="rgba(255,255,255,0.05)" textColor="#94a3b8">Không bảo hành</Badge>
                        ) : warranty ? (
                          (() => {
                            const isExpired = new Date(warranty.expiresAt) <= new Date();
                            const isAuto = warranty.activationReason === 'AUTO_10_DAYS';
                            const daysLeft = Math.ceil((new Date(warranty.expiresAt).getTime() - Date.now()) / 86400000);
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {isExpired ? (
                                    <Badge color="rgba(239, 68, 68, 0.15)" textColor="#f87171" title={`Hết hạn ngày ${formatVnDate(warranty.expiresAt)}`}>
                                      ❌ Hết bảo hành
                                    </Badge>
                                  ) : isAuto ? (
                                    <Badge color="rgba(245, 158, 11, 0.15)" textColor="#fbbf24" title={warranty.activationNote || 'Hệ thống tự động kích hoạt sau 10 ngày xuất kho (tính từ ngày xuất kho + 3 ngày)'}>
                                      🤖 Tự động (XK + 3 ngày)
                                    </Badge>
                                  ) : (
                                    <Badge color="rgba(34, 197, 94, 0.15)" textColor="#4ade80" title="Kích hoạt thủ công bởi người dùng">
                                      👤 Thủ công
                                    </Badge>
                                  )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                  <span style={{ color: '#cbd5e1' }}>{formatVnDate(warranty.activatedAt)}</span>
                                  <span style={{ margin: '0 4px', color: '#64748b' }}>→</span>
                                  <span style={{ color: isExpired ? '#f87171' : '#cbd5e1' }}>{formatVnDate(warranty.expiresAt)}</span>
                                  {!isExpired && (
                                    <span style={{ marginLeft: '6px', color: '#4ade80', fontWeight: 600 }}>
                                      ({daysLeft} ngày)
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          (() => {
                            const createdMs = new Date(d.createdAt).getTime();
                            const autoActivateMs = createdMs + 10 * 24 * 60 * 60 * 1000;
                            const diffDays = Math.ceil((autoActivateMs - Date.now()) / (24 * 60 * 60 * 1000));
                            return diffDays > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Badge color="rgba(234, 179, 8, 0.15)" textColor="#facc15" title={`Tự kích hoạt sau ${diffDays} ngày nữa nếu chưa có người kích hoạt`}>
                                  ⏳ Chờ KH (còn {diffDays} ngày)
                                </Badge>
                                <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                  Tự KH: {formatVnDate(autoActivateMs)} (tính từ XK + 3 ngày)
                                </span>
                              </div>
                            ) : (
                              <Badge color="rgba(249, 115, 22, 0.15)" textColor="#fb923c" title="Đã quá 10 ngày. Hệ thống sẽ tự kích hoạt ở lần truy cập tiếp theo">
                                ⚡ Sẽ tự KH
                              </Badge>
                            );
                          })()
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <DeviceActionDropdown
                          onHistory={() => loadHistory(d.id)}
                          onDelete={() => handleDeleteDeviceClick(d)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {paginatedDevices.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '32px' }}>Không tìm thấy thiết bị nào</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          pageSize={pageSize}
          onPageChange={setPage}
          label="BMCU"
        />
      </div>

      {/* History Modal */}
      {showHistory && (
        <DeviceHistoryModal
          deviceId={showHistory}
          initialData={historyData}
          onClose={() => { setShowHistory(null); setHistoryData(null); }}
          showToast={showToast}
        />
      )}

      {/* Confirm Delete Device Modal */}
      {confirmDeleteDevice && createPortal(
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            background: '#0e121a', border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '420px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Xác nhận xoá</h3>
              <button onClick={() => setConfirmDeleteDevice(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5, marginBottom: '32px' }}>
              Xoá thiết bị <strong style={{ color: '#f8fafc' }}>{confirmDeleteDevice.deviceUid}</strong>? Hành động này không thể hoàn tác!
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setConfirmDeleteDevice(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)',
                color: '#cbd5e1', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: 'pointer',
              }}>
                Hủy
              </button>
              <button type="button" onClick={performDeleteDevice} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: '#dc2626',
                color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)'
              }}>
                Xác nhận xoá
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────
function DashboardTab({ stats, setActiveTab }: { stats: AnalyticsStats, setActiveTab: (tab: Tab) => void }) {
  function renderList(title: string, items: MetricItem[], unit: string) {
    const displayItems = items || [];
    return (
      <div style={{ ...card, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#f8fafc', fontSize: '16px', fontWeight: 600 }}>{title}</h3>
        <div style={{ display: 'grid', gap: '10px', flex: 1, alignContent: 'start' }}>
          {displayItems.length > 0 ? displayItems.map((item, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: '12px' }}>
                <div style={{ color: '#cbd5e1', fontWeight: 500, fontSize: '13px', lineHeight: 1.4 }} title={item.name}>
                  {item.name}
                </div>
              </div>
              <strong style={{ color: 'var(--neon-red)', whiteSpace: 'nowrap', flexShrink: 0, fontSize: '14px', alignSelf: 'center' }}>{item.count} {unit}</strong>
            </div>
          )) : (
            <div style={{ padding: '12px', color: '#94a3b8', fontStyle: 'italic', fontSize: '14px' }}>Chưa có dữ liệu</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="metric-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: '24px' }}>
        <div className="metric-card"><span>Tổng lượt xem</span><strong>{stats.totalViews}</strong><small>Pageviews</small></div>
        <div className="metric-card"><span>Người truy cập</span><strong>{stats.uniqueVisitors}</strong><small>Unique Visitors</small></div>
        <Link to="/products" className="metric-card" style={{ textDecoration: 'none' }}>
          <span>Sản phẩm</span><strong>{stats.productsCount}</strong><small>Quản lý &gt;</small>
        </Link>
        <Link to="/news" className="metric-card" style={{ textDecoration: 'none' }}>
          <span>Bài viết</span><strong>{stats.postsCount}</strong><small>Tin tức &gt;</small>
        </Link>
        <button type="button" onClick={() => setActiveTab('firmwares')} className="metric-card" style={{ textDecoration: 'none', textAlign: 'left', cursor: 'pointer', outline: 'none', width: '100%', border: 'none' }}>
          <span>Firmware</span><strong>{stats.firmwaresCount}</strong><small>Quản lý &gt;</small>
        </button>
      </div>

      <div style={{ ...card, marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#f8fafc', fontSize: '16px', fontWeight: 600 }}>Tăng trưởng truy cập (7 ngày)</h3>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={stats.chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--neon-red)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--neon-red)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--dark-bg)', borderColor: 'var(--glass-border)', color: '#f8fafc', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }} itemStyle={{ color: 'var(--neon-red)' }} />
              <Area type="monotone" dataKey="views" name="Lượt xem" stroke="var(--neon-red)" strokeWidth={2.5} fillOpacity={1} fill="url(#colorViews)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
        {renderList('🏆 Top Sản phẩm xem nhiều', stats.topProducts, 'lượt xem')}
        {renderList('🏆 Top Bài viết xem nhiều', stats.topPosts, 'lượt xem')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
        {renderList('🛒 Sản phẩm được click Mua ngay', stats.topBuys, 'click')}
        {renderList('💾 Firmware được nạp nhiều nhất', stats.topFirmwares, 'lượt nạp')}
      </div>
    </>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab({ showToast }: { showToast: (msg: string, type?: 'info' | 'error' | 'warning') => void }) {
  const [settingsForm, setSettingsForm] = useState({ bmcu_models: '', printer_models: '', slots: '', retract_lengths: '', default_firmware_url: '' });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ settings: any }>('/admin/settings', undefined, true);
      const s = res.settings || {};
      setSettingsForm({
        bmcu_models: (s.bmcu_models || []).join('\n'),
        printer_models: (s.printer_models || []).join('\n'),
        slots: (s.slots || []).join('\n'),
        retract_lengths: (s.retract_lengths || ['0.10', '0.20', '0.25', '0.30', '0.35', '0.40', '0.45', '0.50', '0.55', '0.60', '0.65', '0.70', '0.75', '0.80', '0.85', '0.90']).join('\n'),
        default_firmware_url: s.default_firmware_url || '',
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSaveSettings() {
    try {
      const bmcu = settingsForm.bmcu_models.split('\n').map(s => s.trim()).filter(Boolean);
      const printer = settingsForm.printer_models.split('\n').map(s => s.trim()).filter(Boolean);
      const vars = settingsForm.slots.split('\n').map(s => s.trim()).filter(Boolean);
      const retracts = settingsForm.retract_lengths.split('\n').map(s => s.trim()).filter(Boolean);

      await Promise.all([
        apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'bmcu_models', value: bmcu }) }, true),
        apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'printer_models', value: printer }) }, true),
        apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'slots', value: vars }) }, true),
        apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'retract_lengths', value: retracts }) }, true),
        apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'default_firmware_url', value: settingsForm.default_firmware_url }) }, true)
      ]);

      showToast('Đã lưu cài đặt tuỳ chọn', 'info');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi lưu cài đặt', 'error');
    }
  }

  const [uploadingFw, setUploadingFw] = useState(false);
  const handleUploadFw = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.bin');
    input.click();

    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;
      setUploadingFw(true);
      showToast('Đang tải file lên...', 'info');
      try {
        const fileArray = Array.from(files);
        const urls = await uploadFiles(fileArray);
        if (urls && urls.length > 0) {
          const oldUrl = settingsForm.default_firmware_url;
          setSettingsForm(prev => ({ ...prev, default_firmware_url: urls[0] }));
          await apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'default_firmware_url', value: urls[0] }) }, true);

          if (oldUrl) {
            const oldFilename = oldUrl.split('/').pop();
            if (oldFilename) {
              await apiRequest(`/upload/${oldFilename}`, { method: 'DELETE' }, true).catch(console.error);
            }
          }

          showToast('Tải file và lưu cài đặt thành công!', 'info');
        }
      } catch (e) {
        showToast('Tải file thất bại', 'error');
      } finally {
        setUploadingFw(false);
      }
    };
  };

  const inputStyle: React.CSSProperties = {
    padding: '10px 14px', borderRadius: '10px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid var(--glass-border)',
    color: '#f8fafc',
    fontSize: '14px', width: '100%', boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 700, color: '#f8fafc' }}>Cài đặt Tuỳ chọn Dropdown</h2>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
              Tùy chỉnh các danh sách giá trị hiển thị trong các dropdown trên toàn hệ thống.
            </p>
          </div>
          <button onClick={handleSaveSettings} style={{
            padding: '10px 24px', borderRadius: '10px', background: 'var(--neon-red)',
            color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
            fontSize: '14px', boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)',
            whiteSpace: 'nowrap'
          }}>Lưu Cài Đặt</button>
        </div>

        {loading ? <FormSkeletonLoader /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <label style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', display: 'block', marginBottom: '8px' }}>
                Model BMCU
              </label>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Mỗi dòng 1 tuỳ chọn</p>
              <textarea style={{ ...inputStyle, minHeight: '180px', resize: 'vertical', background: 'rgba(0,0,0,0.2)' }} value={settingsForm.bmcu_models} onChange={e => setSettingsForm({ ...settingsForm, bmcu_models: e.target.value })} placeholder="Ví dụ:&#10;BMCU-370X Type C&#10;BMCU-370X Pro" />
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <label style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', display: 'block', marginBottom: '8px' }}>
                Chế độ nạp (Load Mode)
              </label>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Mỗi dòng 1 tuỳ chọn</p>
              <textarea style={{ ...inputStyle, minHeight: '180px', resize: 'vertical', background: 'rgba(0,0,0,0.2)' }} value={settingsForm.printer_models} onChange={e => setSettingsForm({ ...settingsForm, printer_models: e.target.value })} placeholder="Ví dụ:&#10;A1/A1 Mini (Standard)" />
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <label style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', display: 'block', marginBottom: '8px' }}>
                Vị trí AMS (AMS Slot)
              </label>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Mỗi dòng 1 tuỳ chọn</p>
              <textarea style={{ ...inputStyle, minHeight: '180px', resize: 'vertical', background: 'rgba(0,0,0,0.2)' }} value={settingsForm.slots} onChange={e => setSettingsForm({ ...settingsForm, slots: e.target.value })} placeholder="Ví dụ:&#10;SOLO&#10;AMS_A" />
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
              <label style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', display: 'block', marginBottom: '8px' }}>
                Độ lùi sợi (Retract Length)
              </label>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Mỗi dòng 1 tuỳ chọn</p>
              <textarea style={{ ...inputStyle, minHeight: '180px', resize: 'vertical', background: 'rgba(0,0,0,0.2)' }} value={settingsForm.retract_lengths} onChange={e => setSettingsForm({ ...settingsForm, retract_lengths: e.target.value })} placeholder="Ví dụ:&#10;0.10&#10;0.20" />
            </div>
          </div>
        )}
      </div>

      <div style={{ background: 'var(--glass-bg)', borderRadius: '16px', padding: '24px', border: '1px solid var(--glass-border)', marginTop: '40px', boxShadow: '0 12px 24px rgba(0,0,0,0.3)' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 700, color: '#f8fafc' }}>Firmware Cơ Bản (Default Firmware)</h2>
        <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px' }}>
          File firmware định dạng `.bin` được dùng để nạp vào thiết bị BMCU ngay sau khi thêm mới.
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            readOnly
            style={{ ...inputStyle, background: 'rgba(255,255,255,0.05)', flex: 1, color: '#cbd5e1' }}
            value={settingsForm.default_firmware_url || 'Chưa cấu hình'}
          />
          <button
            onClick={handleUploadFw}
            disabled={uploadingFw}
            style={{
              padding: '10px 16px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)',
              color: '#f8fafc', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: uploadingFw ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {uploadingFw ? 'Đang tải...' : 'Tải lên .bin'}
          </button>
          <button
            onClick={async () => {
              const oldUrl = settingsForm.default_firmware_url;
              setSettingsForm(prev => ({ ...prev, default_firmware_url: '' }));
              try {
                await apiRequest('/admin/settings', { method: 'POST', body: JSON.stringify({ key: 'default_firmware_url', value: '' }) }, true);

                if (oldUrl) {
                  const oldFilename = oldUrl.split('/').pop();
                  if (oldFilename) {
                    await apiRequest(`/upload/${oldFilename}`, { method: 'DELETE' }, true).catch(console.error);
                  }
                }

                showToast('Đã xoá firmware mặc định', 'info');
              } catch (e) {
                showToast('Lỗi xoá cài đặt firmware', 'error');
              }
            }}
            style={{
              padding: '10px 16px', borderRadius: '10px',
              background: 'rgba(225, 29, 72, 0.15)',
              color: '#f43f5e',
              border: '1px solid rgba(225, 29, 72, 0.3)',
              fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Xoá
          </button>
        </div>
      </div>

      <ImageManager showToast={showToast} />
    </div>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────
export default function AdminPage() {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const user = getAuthUser();
  const token = getAuthToken();

  useEffect(() => {
    if (!token || user?.role !== 'ADMIN') { setLoading(false); return; }
    apiRequest<AnalyticsStats>('/analytics/stats', undefined, true)
      .then(setStats)
      .catch(e => showToast(e instanceof Error ? e.message : 'Lỗi tải Dashboard', 'error'))
      .finally(() => setLoading(false));
  }, [token, user?.role]);

  if (!token || !user || user.role !== 'ADMIN') {
    return (
      <div className="page-block" style={{ textAlign: 'center', padding: '80px 24px' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🔒</div>
        <h1 style={{ color: '#fff', fontSize: '2rem', fontWeight: 800, marginBottom: '12px' }}>
          Truy cập bị từ chối
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '1rem', maxWidth: '480px', margin: '0 auto 24px', lineHeight: 1.6 }}>
          Bạn không có quyền truy cập trang quản trị này. Vui lòng đăng nhập bằng tài khoản Quản trị viên (ADMIN).
        </p>
        <Link to="/" className="button primary" style={{ display: 'inline-flex', padding: '12px 28px', borderRadius: '12px' }}>
          Quay lại Trang chủ
        </Link>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: string; badge?: number }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'home', label: 'Trang chủ', icon: '🏠' },
    { id: 'users', label: 'Người dùng', icon: '👥' },
    { id: 'devices', label: 'BMCU', icon: '🔧' },
    { id: 'firmwares', label: 'Firmware', icon: '💾' },
    { id: 'tickets', label: 'Yêu cầu Hỗ trợ', icon: '🎫', badge: stats?.openTicketsCount },
    { id: 'settings', label: 'Cài đặt', icon: '⚙️' },
  ];

  return (
    <div className="page-block admin-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <span className="eyebrow">Admin Portal</span>
          <h1 style={{ margin: '8px 0 0', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>Quản trị hệ thống</h1>
        </div>
        <button type="button" className="button secondary" onClick={() => { clearAuthToken(); window.location.href = '/'; }}>
          Đăng xuất
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', borderBottom: '2px solid rgba(255,255,255,0.1)', paddingBottom: '0' }}>
        {tabs.map(t => (
          <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} style={{
            padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === t.id ? 700 : 500, fontSize: '15px',
            color: activeTab === t.id ? 'var(--neon-red)' : '#94a3b8',
            borderBottom: activeTab === t.id ? '2px solid var(--neon-red)' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.15s',
          }}>
            {t.icon} {t.label}
            {t.badge ? (
              <span style={{ marginLeft: '6px', background: '#dc2626', color: 'white', padding: '2px 6px', borderRadius: '99px', fontSize: '11px', fontWeight: 700 }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'dashboard' && (
        loading ? <DashboardSkeletonLoader /> : stats ? <DashboardTab stats={stats} setActiveTab={setActiveTab} /> : null
      )}
      {activeTab === 'users' && <UsersTab showToast={showToast} />}
      {activeTab === 'home' && <HomeBuilderTab showToast={showToast} />}
      {activeTab === 'devices' && <DevicesTab showToast={showToast} />}
      {activeTab === 'firmwares' && <FirmwaresTab showToast={showToast} />}
      {activeTab === 'tickets' && <TicketsTab showToast={showToast} />}
      {activeTab === 'settings' && <SettingsTab showToast={showToast} />}
    </div>
  );
}

// ─── Tickets Tab ─────────────────────────────────────────────────────────────
function TicketsTab({ showToast }: { showToast: (msg: string, type?: 'info' | 'error' | 'warning') => void }) {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ tickets: any[] }>('/admin/tickets', undefined, true);
      setTickets(res.tickets);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  async function updateStatus(id: string, status: string) {
    setBusy(id);
    try {
      await apiRequest(`/admin/tickets/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }, true);
      setTickets(prev => prev.map(ticket => ticket.id === id ? { ...ticket, status } : ticket));
      showToast('Đã cập nhật trạng thái', 'info');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi cập nhật', 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={card}>
      <SectionTitle title="Yêu cầu Hỗ trợ (Bảo hành)" count={tickets.length} />
      {loading ? <CardSkeletonLoader count={3} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {tickets.map(t => (
            <div key={t.id} style={{ border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '20px', background: 'rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>{t.title}</div>
                  <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>
                    Ngày gửi: {formatVnDateTime(t.createdAt)}
                  </div>
                </div>
                <div>
                  {t.status === 'open' && <Badge color="rgba(245, 158, 11, 0.1)" textColor="#fbbf24">Chờ xử lý</Badge>}
                  {t.status === 'processing' && <Badge color="rgba(56, 189, 248, 0.1)" textColor="#7dd3fc">Đang xử lý</Badge>}
                  {t.status === 'closed' && <Badge color="rgba(34, 197, 94, 0.1)" textColor="#4ade80">Đã hoàn tất</Badge>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '20px' }}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '16px', border: '1px solid var(--glass-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Thông tin khách hàng</div>
                  <div style={{ fontWeight: 600, color: '#f8fafc', marginBottom: '4px' }}>{t.user?.name || t.user?.email}</div>
                  <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    📞 {t.user?.phone || 'Chưa cập nhật SĐT'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ✉️ {t.user?.email}
                  </div>
                </div>

                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '16px', border: '1px solid var(--glass-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Mô tả lỗi & Địa chỉ</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', color: '#cbd5e1', lineHeight: 1.6 }}>
                    {t.description}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                {t.status === 'open' && (
                  <button className="button primary" style={{ padding: '10px 20px' }} onClick={() => updateStatus(t.id, 'processing')} disabled={busy === t.id}>
                    {busy === t.id ? 'Đang xử lý...' : 'Tiếp nhận xử lý'}
                  </button>
                )}
                {t.status === 'processing' && (
                  <button className="button primary" style={{ padding: '10px 20px', background: '#16a34a', borderColor: '#16a34a' }} onClick={() => updateStatus(t.id, 'closed')} disabled={busy === t.id}>
                    {busy === t.id ? 'Đang cập nhật...' : 'Hoàn tất bảo hành'}
                  </button>
                )}
                {t.status === 'closed' && (
                  <button className="button outline" style={{ padding: '10px 20px', color: '#64748b' }} onClick={() => updateStatus(t.id, 'open')} disabled={busy === t.id}>
                    {busy === t.id ? 'Đang cập nhật...' : 'Mở lại yêu cầu'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {tickets.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', background: '#f8fafc', borderRadius: '16px' }}>
              Không có yêu cầu bảo hành nào
            </div>
          )}
        </div>
      )}
    </div>
  );
}
